<template>
  <v-card outlined class="block">
    <h3>{{ $t('hedgeOS.dashboard.aggregateHedge.chartTitle') }} <span class="meta">{{ $t('hedgeOS.dashboard.aggregateHedge.chartMeta') }}</span></h3>
    <div class="chart-wrap">
      <svg viewBox="0 0 600 240" preserveAspectRatio="none">
        <line class="grid-line" x1="0" y1="60" x2="600" y2="60"/>
        <line class="grid-line" x1="0" y1="120" x2="600" y2="120"/>
        <line class="grid-line" x1="0" y1="180" x2="600" y2="180"/>
        <line x1="0" y1="120" x2="600" y2="120" stroke="#8a857a" stroke-dasharray="2,4"/>

        <!-- FX leg -->
        <path fill="none" stroke="#7bc4a4" stroke-width="1.2" stroke-dasharray="3,3" opacity="0.7" d="M0,120 L60,115 L120,108 L180,100 L240,94 L300,85 L360,82 L420,72 L480,68 L540,65 L600,62"/>
        <!-- Commodity leg -->
        <path fill="none" stroke="#e8b64a" stroke-width="1.2" stroke-dasharray="3,3" opacity="0.7" d="M0,120 L60,118 L120,114 L180,110 L240,105 L300,98 L360,92 L420,85 L480,78 L540,72 L600,68"/>

        <!-- Combined -->
        <path fill="rgba(255,77,31,0.08)" d="M0,120 L20,110 L40,115 L70,95 L100,98 L140,85 L180,88 L220,72 L260,68 L300,78 L340,60 L380,55 L420,48 L460,52 L500,40 L540,35 L580,28 L600,30 L600,120 L0,120 Z"/>
        <path fill="none" stroke="#ff4d1f" stroke-width="2" d="M0,120 L20,110 L40,115 L70,95 L100,98 L140,85 L180,88 L220,72 L260,68 L300,78 L340,60 L380,55 L420,48 L460,52 L500,40 L540,35 L580,28 L600,30"/>
        <circle cx="600" cy="30" r="4" fill="#ff4d1f"/>
        <circle cx="600" cy="30" r="8" fill="#ff4d1f" opacity="0.3"/>

        <text class="axis-label" x="0" y="236">09:00</text>
        <text class="axis-label" x="150" y="236">10:30</text>
        <text class="axis-label" x="300" y="236">12:00</text>
        <text class="axis-label" x="450" y="236">13:30</text>
        <text class="axis-label" x="560" y="236">14:32</text>
        <text class="axis-label" x="5" y="60">+R$3.8M</text>
        <text class="axis-label" x="5" y="125" fill="#8a857a">0</text>
        <text class="axis-label" x="5" y="180">-R$1.5M</text>

        <text class="axis-label" x="495" y="55" fill="#ff4d1f">{{ $t('hedgeOS.dashboard.aggregateHedge.chartCombined') }}</text>
        <text class="axis-label" x="520" y="78" fill="#7bc4a4">{{ $t('hedgeOS.dashboard.aggregateHedge.chartFx') }}</text>
        <text class="axis-label" x="520" y="90" fill="#e8b64a">{{ $t('hedgeOS.dashboard.aggregateHedge.chartCommodity') }}</text>
      </svg>
    </div>

    <h3 style="margin-top:2rem;">{{ $t('hedgeOS.dashboard.aggregateHedge.summaryTitle') }} <span class="meta">{{ $t('hedgeOS.dashboard.aggregateHedge.summaryMeta') }}</span></h3>
    <div class="risk-summary">
      <div v-for="factor in riskFactors" :key="factor.label" class="risk-row">
        <span style="color:var(--dim);">{{ factor.label }}</span>
        <div class="bar">
          <div class="center-line"></div>
          <div
            class="fill"
            :class="factor.tone"
            :style="{ [factor.side]: '50%', width: factor.width + '%' }"
          ></div>
          <div
            class="hedged"
            :style="{ [factor.side]: '50%', width: factor.hedgedWidth + '%' }"
          ></div>
        </div>
        <span style="text-align:right;" :class="factor.tone">{{ factor.value }}</span>
      </div>

      <div class="legend">
        <div><span class="sw long"></span>{{ $t('hedgeOS.dashboard.aggregateHedge.legendLong') }}</div>
        <div><span class="sw short"></span>{{ $t('hedgeOS.dashboard.aggregateHedge.legendShort') }}</div>
        <div><span class="sw hedge"></span>{{ $t('hedgeOS.dashboard.aggregateHedge.legendHedged') }}</div>
      </div>
    </div>
</v-card>
</template>

<script>
export default {
  name: "AggregateHedge",
  data() {
    return {
      riskFactors: [
        { label: "USD", side: "right", tone: "neg", width: 24, hedgedWidth: 18, value: "- 12.8M" },
        { label: "EUR", side: "left", tone: "pos", width: 16, hedgedWidth: 12, value: "+ 4.1M" },
        { label: "LME · AL", side: "right", tone: "neg", width: 14, hedgedWidth: 8, value: "- 800t net" },
        { label: "CBOT · SOY", side: "left", tone: "pos", width: 18, hedgedWidth: 16, value: "+ 2,500t" },
        { label: "ICE-C · COFFEE", side: "left", tone: "pos", width: 8, hedgedWidth: 3, value: "+ 320 sc" },
      ],
    };
  },
};
</script>

<style scoped>
.block {
  background: var(--ink);
  padding: 2rem;
}

.block h3 {
  font-family: 'Fraunces', serif;
  font-weight: 600;
  font-size: 0.95rem;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  margin-bottom: 1.5rem;
  padding-bottom: 0.8rem;
  border-bottom: 1px solid var(--line);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.block h3 .meta {
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.65rem;
  color: var(--muted);
  font-weight: 400;
  letter-spacing: 0.15em;
  text-transform: uppercase;
}

.chart-wrap {
  position: relative;
  height: 260px;
  margin-top: 0.5rem;
}

.chart-wrap svg {
  width: 100%;
  height: 100%;
}

.axis-label {
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px;
  fill: var(--muted);
}

.grid-line {
  stroke: var(--grid);
  stroke-width: 1;
}

.risk-summary {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  padding-top: 0.5rem;
}

.risk-row {
  display: grid;
  grid-template-columns: 110px 1fr 100px;
  gap: 0.8rem;
  align-items: center;
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.74rem;
}

.risk-row .bar {
  height: 8px;
  background: var(--ink2);
  border: 1px solid var(--line);
  position: relative;
}

.bar .center-line {
  position: absolute;
  left: 50%;
  top: 0;
  bottom: 0;
  width: 1px;
  background: var(--dim);
}

.bar .fill {
  position: absolute;
  top: 0;
  bottom: 0;
}

.bar .fill.pos {
  background: var(--cool);
}

.bar .fill.neg {
  background: var(--hot);
}

.bar .hedged {
  position: absolute;
  top: 0;
  bottom: 0;
  background: repeating-linear-gradient(45deg, rgba(243, 239, 230, 0.25), rgba(243, 239, 230, 0.25) 3px, transparent 3px, transparent 6px);
}

.pos {
  color: var(--cool);
}

.neg {
  color: var(--hot);
}

.legend {
  display: flex;
  gap: 1.5rem;
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.6rem;
  color: var(--muted);
  letter-spacing: 0.1em;
  text-transform: uppercase;
  padding-top: 1rem;
  margin-top: 0.5rem;
  border-top: 1px solid var(--line);
}

.legend .sw {
  display: inline-block;
  width: 10px;
  height: 6px;
  margin-right: 0.4rem;
  vertical-align: middle;
}

.sw.long {
  background: var(--cool);
}

.sw.short {
  background: var(--hot);
}

.sw.hedge {
  background: repeating-linear-gradient(45deg, var(--dim), var(--dim) 2px, transparent 2px, transparent 4px);
}
</style>
